/**
 * Customer-facing connections RPC definitions (CLA-429).
 *
 * The same connect / reconnect / revoke / codex-capture mechanics the admin
 * Connections tab drives, re-exposed under token/Clerk auth ({@link AuthMiddleware},
 * NOT `AdminMiddleware`) so the customer onboarding wizard can drive them for
 * the caller's OWN agent. Every endpoint takes an `agentId`; the handler
 * verifies the agent belongs to a customer the caller is a member of and fails
 * {@link Forbidden} otherwise. The codex-capture endpoints take the agent
 * implicitly — no `installationName` leaks onto the wire — resolving the
 * installation from the verified agent.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import {
  ConnectionStatus,
  GoogleDriveAccess,
  GoogleDrivePickedItem,
  InstallationConnection,
} from "@fm/db/models/InstallationConnection";
import { Schema } from "effect";
import { ConnectStatusResponse, ExchangeMailAccountConfig } from "../admin/installation-connections.rpc.ts";
import {
  OAuthRpcError,
  PollDeviceAuthInput,
  PollDeviceAuthResponse,
  StartDeviceAuthResponse,
} from "../admin/oauth-providers.rpc.ts";
import { AuthMiddleware } from "../auth.tags.ts";
import { CryptoError, DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";
import {
  WhatsappCancelPairingResponse,
  WhatsappPairingInput,
  WhatsappPairingRpcError,
  WhatsappPollPairingResponse,
  WhatsappStartPairingResponse,
} from "../whatsapp-pairing.rpc.ts";

// ============================================================================
// Inputs
// ============================================================================

/**
 * Single-agent input for the connect-status / codex-capture endpoints. The
 * agent must belong to a customer the caller is a member of.
 *
 * @since 1.0.0
 */
export const ConnectionsAgentInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
});
export type ConnectionsAgentInput = Schema.Schema.Type<typeof ConnectionsAgentInput>;

/**
 * Scope a connection mutation (revoke / reconnect) to a single
 * `(agentId, provider, accountKey)` triple. `accountKey` defaults to the empty
 * string for single-account providers.
 *
 * @since 1.0.0
 */
export const ConnectionsAgentScopeInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  provider: Schema.NonEmptyTrimmedString,
  accountKey: Schema.optional(Schema.String),
});
export type ConnectionsAgentScopeInput = Schema.Schema.Type<typeof ConnectionsAgentScopeInput>;

/**
 * Start a per-provider OAuth round-trip for a channel/connector on the caller's
 * own agent. The installation is resolved server-side from the verified
 * `agentId` — no installation id/name crosses the wire — and handed to the
 * shared connect machinery ({@link InstallOAuth}). `surfaces` narrows an
 * umbrella provider's scope set (e.g. only Gmail of the Google catalog).
 *
 * @since 1.0.0
 */
export const ConnectionsOAuthStartInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  provider: Schema.NonEmptyTrimmedString,
  surfaces: Schema.optional(Schema.Array(Schema.String)),
  /**
   * Google Drive access mode (CLA drive-access). `"picked"` narrows the Drive
   * scope to `drive.file` (Picker-chosen items only); `"full"` keeps the
   * restricted full-Drive scope. Ignored for non-Drive flows.
   */
  driveAccess: Schema.optional(GoogleDriveAccess),
});
export type ConnectionsOAuthStartInput = Schema.Schema.Type<typeof ConnectionsOAuthStartInput>;

/**
 * Store the Google Picker selection onto an already-connected google connection
 * that was granted `"picked"` Drive access, on the caller's own agent. The
 * connection is resolved within the verified installation's own rows by
 * `(provider = "google", accountKey)` — never by a client-supplied connection
 * id. Items are capped at 200; only the non-secret file/folder ids + names are
 * persisted (into the row's `metadata.drive.items`), never a token.
 *
 * @since 1.0.0
 */
export const ConnectionsGoogleDriveSelectionInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  accountKey: Schema.optional(Schema.String),
  items: Schema.Array(GoogleDrivePickedItem).pipe(Schema.maxItems(200)),
});
export type ConnectionsGoogleDriveSelectionInput = Schema.Schema.Type<typeof ConnectionsGoogleDriveSelectionInput>;

/**
 * Acknowledgment that the Google Picker selection persisted, echoing the number
 * of items stored.
 *
 * @since 1.0.0
 */
export const ConnectionsGoogleDriveSelectionResponse = Schema.Struct({
  success: Schema.Boolean,
  itemCount: Schema.Int,
});
export type ConnectionsGoogleDriveSelectionResponse = Schema.Schema.Type<
  typeof ConnectionsGoogleDriveSelectionResponse
>;

/**
 * The authorize URL the browser redirects to. The opaque `state` minted inside
 * the URL is the only handle the {@link ConnectionsOAuthCallbackInput} needs.
 *
 * @since 1.0.0
 */
export const ConnectionsOAuthStartResponse = Schema.Struct({
  authUrl: Schema.String,
});
export type ConnectionsOAuthStartResponse = Schema.Schema.Type<typeof ConnectionsOAuthStartResponse>;

/**
 * Finish a per-provider OAuth round-trip. Self-contained: the opaque `state`
 * (minted by start, stored server-side alongside the PKCE verifier +
 * installation id) is the secret that recovers and scopes the exchange, so no
 * `agentId` is required — mirroring `connections/codexPollDeviceAuth`.
 *
 * @since 1.0.0
 */
export const ConnectionsOAuthCallbackInput = Schema.Struct({
  provider: Schema.NonEmptyTrimmedString,
  code: Schema.String,
  state: Schema.String,
});
export type ConnectionsOAuthCallbackInput = Schema.Schema.Type<typeof ConnectionsOAuthCallbackInput>;

/**
 * Acknowledgment that the OAuth callback persisted the connection.
 *
 * @since 1.0.0
 */
export const ConnectionsOAuthCallbackResponse = Schema.Struct({
  success: Schema.Boolean,
});
export type ConnectionsOAuthCallbackResponse = Schema.Schema.Type<typeof ConnectionsOAuthCallbackResponse>;

/**
 * Save an API-key-backed connection (Telegram / WhatsApp / Linear / Jira /
 * Notion …) for a channel/connector on the caller's own agent. Scoped to the
 * verified installation, same as the OAuth start.
 *
 * @since 1.0.0
 */
export const ConnectionsSaveApiKeyInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  provider: Schema.NonEmptyTrimmedString,
  apiKey: Schema.NonEmptyTrimmedString,
  metadata: Schema.optional(Schema.Unknown),
});
export type ConnectionsSaveApiKeyInput = Schema.Schema.Type<typeof ConnectionsSaveApiKeyInput>;

/**
 * Acknowledgment that the API key persisted.
 *
 * @since 1.0.0
 */
export const ConnectionsSaveApiKeyResponse = Schema.Struct({
  success: Schema.Boolean,
});
export type ConnectionsSaveApiKeyResponse = Schema.Schema.Type<typeof ConnectionsSaveApiKeyResponse>;

/**
 * Save an exchange-mail (`type: "email"`, `provider: "exchange-mail"`)
 * connection for the caller's OWN agent. The non-secret account config lands in
 * the row's `metadata`; `imapPassword` / `smtpPassword` are encrypted into the
 * `secretFields` envelope server-side (never persisted in `metadata`, never
 * logged). The installation is resolved + ownership-checked from the verified
 * `agentId`, mirroring {@link ConnectionsSaveApiKeyInput}.
 *
 * @since 1.0.0
 */
export const ConnectionsUpsertEmailInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  provider: Schema.Literal("exchange-mail"),
  accountKey: Schema.optional(Schema.String),
  status: Schema.optional(ConnectionStatus),
  accountLabel: Schema.optional(Schema.NullOr(Schema.String)),
  config: ExchangeMailAccountConfig,
  imapPassword: Schema.NonEmptyTrimmedString,
  smtpPassword: Schema.NonEmptyTrimmedString,
});
export type ConnectionsUpsertEmailInput = Schema.Schema.Type<typeof ConnectionsUpsertEmailInput>;

// ============================================================================
// WhatsApp pairing (CLA-98)
// ============================================================================

/**
 * The WhatsApp pairing schemas + wire error are shared with the admin
 * installation-connections rail (CLA-707), so they live in
 * {@link file://../whatsapp-pairing.rpc.ts} — a neutral module neither RPC
 * group imports the other through — and are re-exported here so existing
 * consumers keep their import path.
 *
 * @since 1.0.0
 */
export {
  WhatsappCancelPairingResponse,
  WhatsappPairingInput,
  WhatsappPairingRpcError,
  WhatsappPollPairingResponse,
  WhatsappStartPairingResponse,
};

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Customer-facing connections RPC group. Every endpoint requires a valid
 * token/Clerk session via {@link AuthMiddleware} and is scoped to the caller's
 * own agent. Tags are namespaced under `connections/`.
 *
 * @since 1.0.0
 */
export class ConnectionsRpcs extends RpcGroup.make(
  Rpc.make("connections/connectStatus", {
    payload: ConnectionsAgentInput,
    success: ConnectStatusResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/revoke", {
    payload: ConnectionsAgentScopeInput,
    success: InstallationConnection.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/reconnect", {
    payload: ConnectionsAgentScopeInput,
    success: InstallationConnection.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/codexStartDeviceAuth", {
    payload: ConnectionsAgentInput,
    success: StartDeviceAuthResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/codexPollDeviceAuth", {
    payload: PollDeviceAuthInput,
    success: PollDeviceAuthResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/oauthStart", {
    payload: ConnectionsOAuthStartInput,
    success: ConnectionsOAuthStartResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/oauthCallback", {
    payload: ConnectionsOAuthCallbackInput,
    success: ConnectionsOAuthCallbackResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/saveApiKey", {
    payload: ConnectionsSaveApiKeyInput,
    success: ConnectionsSaveApiKeyResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/googleDriveSelection", {
    payload: ConnectionsGoogleDriveSelectionInput,
    success: ConnectionsGoogleDriveSelectionResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, OAuthRpcError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/upsertEmail", {
    payload: ConnectionsUpsertEmailInput,
    success: InstallationConnection.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, CryptoError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/whatsappStartPairing", {
    payload: WhatsappPairingInput,
    success: WhatsappStartPairingResponse,
    error: Schema.Union(WhatsappPairingRpcError, Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/whatsappPollPairing", {
    payload: WhatsappPairingInput,
    success: WhatsappPollPairingResponse,
    error: Schema.Union(WhatsappPairingRpcError, Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("connections/whatsappCancelPairing", {
    payload: WhatsappPairingInput,
    success: WhatsappCancelPairingResponse,
    error: Schema.Union(WhatsappPairingRpcError, Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
) {}
